import { useEffect, useState } from "react";
import type { DeskSession } from "../hooks/useDeskSession";
import { nextOpId } from "../lib/edit-helpers";
import { coachLineFor, playerFacingCastLabel } from "./tools.js";
import { ToolCartButton } from "./ToolCartButton.js";

export function CastTool({ session }: { session: DeskSession }) {
  const { pack, packId, resolved, applyOp } = session;
  const slots = pack.castSlots ?? [];

  const [slotId, setSlotId] = useState(slots[0]?.id ?? "");

  useEffect(() => {
    const nextSlots = pack.castSlots ?? [];
    setSlotId(nextSlots[0]?.id ?? "");
  }, [packId, pack]);

  if (slots.length === 0) {
    return (
      <div className="tool-panel kid-tool" data-testid="cast-tool">
        <h3>Cast</h3>
        <p className="coach-line" data-testid="cast-unavailable">
          This story has no cast choices.
        </p>
      </div>
    );
  }

  const slot = slots.find((s) => s.id === slotId) ?? slots[0]!;
  const active = resolved.casts.get(slot.id);
  const selectedVariantId = active?.variantId ?? slot.defaultVariantId;
  const disclose = active?.showReconstructionDisclosure ?? true;

  const apply = (nextVariantId: string, nextDisclose = disclose) => {
    applyOp({
      kind: "cast",
      opId: nextOpId("cast"),
      slotId: slot.id,
      variantId: nextVariantId,
      showReconstructionDisclosure: nextDisclose,
    });
  };

  return (
    <div className="tool-panel kid-tool" data-testid="cast-tool">
      <ToolCartButton tool="cast" />
      <h3>Cast</h3>
      <p className="coach-line" data-testid="coach-line">
        {coachLineFor("cast")}
      </p>
      {slots.length > 1 && (
        <label>
          Who
          <select
            value={slot.id}
            aria-label="Cast slot"
            onChange={(e) => {
              const next = slots.find((s) => s.id === e.target.value)!;
              setSlotId(next.id);
            }}
          >
            {slots.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      )}
      <div className="cast-card-grid" data-testid="cast-variants">
        {slot.variants.map((v) => {
          const selected = selectedVariantId === v.id;
          const face = playerFacingCastLabel(v);
          return (
            <button
              key={v.id}
              type="button"
              className={`cast-card${selected ? " active" : ""}`}
              data-testid={`cast-card-${v.id}`}
              aria-pressed={selected}
              onClick={() => apply(v.id)}
            >
              <strong>{face}</strong>
              {selected && <span className="cast-selected-mark">Selected</span>}
            </button>
          );
        })}
      </div>
      <label className="cast-disclose switch-row">
        <span>Label as reconstruction</span>
        <input
          type="checkbox"
          checked={disclose}
          data-testid="cast-disclosure"
          aria-label="Label as reconstruction"
          onChange={(e) => {
            apply(selectedVariantId, e.target.checked);
          }}
        />
      </label>
    </div>
  );
}
